import cn from 'classnames';
import { Suspense, useState } from 'react';
import { Outlet } from 'react-router';
import { DEFAULT_AUDIT_LOG_PAGE_SIZE } from '#api/audit-logs.ts';
import { AuditLogList } from '#components/patterns/audit-log/list/AuditLogList.tsx';
import { useCapitalizedTargetTypeNoun } from '#components/patterns/audit-log/type-string-maps.ts';
import { TableLayout } from '#components/patterns/layouts/table/TableLayout.tsx';
import { TableFooter } from '#components/patterns/table/table-footer/TableFooter.tsx';
import type { MetadataNoCounts } from '#dn-types/api.ts';
import { useSavedPageSize } from '#hooks/useSavedPageSize.tsx';
import { useUrlParams } from '#hooks/useUrlParams.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { AuditLogsPageHeader } from '#screens/logs/audit/audit-logs-page/AuditLogsPageHeader.tsx';
import { Titled } from '#util/title.ts';
import { validateTargetOptionParam } from '../form-types.ts';
import styles from './AuditLogsPage.module.css';

const PARAM_SCHEMA = {
  cursor: 'scalar',
  targetType: 'scalar',
  targetId: 'scalar',
} as const;

export function AuditLogsPage() {
  const [pageSize, setPageSize] = useSavedPageSize({ defaultPageSize: DEFAULT_AUDIT_LOG_PAGE_SIZE });
  const [params, setParams] = useUrlParams(PARAM_SCHEMA);
  const { cursor, targetType: targetTypeParam, targetId } = params;
  const validatedTargetTypeParam = validateTargetOptionParam(targetTypeParam);
  const targetType = validatedTargetTypeParam === 'all' ? undefined : validatedTargetTypeParam;
  const targetNoun = useCapitalizedTargetTypeNoun(targetType);
  const [isFetching, setIsFetching] = useState(false);
  const [metadata, setMetadata] = useState<MetadataNoCounts | undefined>(undefined);
  const [isEmpty, setIsEmtpy] = useState<boolean>(false);

  return (
    <Titled title={(title) => (validatedTargetTypeParam === 'all' ? title : `${targetNoun} ${title}`)}>
      <TableLayout
        isEmpty={isEmpty}
        header={<AuditLogsPageHeader />}
        pagination={
          metadata ?
            <TableFooter
              entityType="logs"
              pageSize={pageSize}
              metadata={metadata}
              isFetching={isFetching}
              setCursor={(cur?: string) => {
                setParams({ cursor: cur });
              }}
              onPageSizeChange={setPageSize}
            />
          : null
        }
      >
        <Suspense fallback={<LoadingSpinner />}>
          <AuditLogList
            className={cn(styles.AuditLogsPage_table)}
            logsQueryArgs={{ cursor, pageSize, targetType, targetId }}
            setIsFetching={setIsFetching}
            setMetadata={setMetadata}
            setIsEmpty={setIsEmtpy}
          />
        </Suspense>
      </TableLayout>
      <Outlet />
    </Titled>
  );
}
